import type { ScriptureReference } from "~/sanity/sanity.types";
import { normalizeInlineWhitespace } from "./utils";

export type ScriptureVerse = {
  content: string;
  reference: {
    book: string;
    chapter: string;
    verse: string;
  };
};

export type RenderableScriptureSegment = {
  content: string;
  verse: string | null;
};

export type RenderableScriptureParagraph = {
  segments: Array<RenderableScriptureSegment>;
};

export type RenderableScriptureContent = {
  emptyState: string | null;
  paragraphs: Array<RenderableScriptureParagraph>;
};

export const SCRIPTURE_EMPTY_STATE_COPY = "Scripture text is not available yet.";

type ScriptureReferenceInput = {
  book?: ScriptureReference["book"] | null;
  startChapter?: number | null;
  startVerse?: number | null;
  endChapter?: number | null;
  endVerse?: number | null;
};

export function formatScriptureReference(passage: ScriptureReferenceInput) {
  const book = passage.book?.replace("Psalms", "Psalm");
  if (!passage.startChapter) return "";
  let passageStr = passage.startVerse
    ? `${book ?? ""} ${passage.startChapter.toString()}:${passage.startVerse.toString()}`
    : `${book ?? ""} ${passage.startChapter.toString()}`;

  const endChapter = passage.endChapter ?? passage.startChapter;

  if (passage.endVerse) {
    if (endChapter === passage.startChapter) {
      passageStr += `-${passage.endVerse.toString()}`;
    } else {
      passageStr += `-${endChapter.toString()}:${passage.endVerse.toString()}`;
    }
  }

  if (passage.endChapter && !passage.endVerse) {
    if (passage.endChapter !== passage.startChapter) {
      passageStr += `-${passage.endChapter.toString()}`;
    }
  }

  return passageStr;
}

export function parseScriptureVerses(esvText: string | null | undefined): Array<ScriptureVerse> {
  if (typeof esvText !== "string" || esvText.length === 0) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(esvText);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed as Array<ScriptureVerse>;
  } catch {
    return [];
  }
}

function splitPlainTextIntoParagraphs(esvText: string | null | undefined): Array<string> {
  if (typeof esvText !== "string" || esvText.trim().length === 0) {
    return [];
  }

  return esvText
    .trim()
    .split(/\n\s*\n+/)
    .map(normalizeInlineWhitespace)
    .filter((paragraph) => paragraph.length > 0);
}

function buildRenderableParagraphsFromVerses(verses: Array<ScriptureVerse>): Array<RenderableScriptureParagraph> {
  const paragraphs: Array<RenderableScriptureParagraph> = [];
  let currentSegments: Array<RenderableScriptureSegment> = [];

  const pushCurrentParagraph = () => {
    if (currentSegments.length === 0) {
      return;
    }

    paragraphs.push({ segments: currentSegments });
    currentSegments = [];
  };

  for (const verse of verses) {
    const chunks = verse.content
      .split(/\n\s*\n+/)
      .map(normalizeInlineWhitespace)
      .filter((chunk) => chunk.length > 0);

    if (chunks.length === 0) {
      continue;
    }

    chunks.forEach((chunk, index) => {
      currentSegments.push({
        content: chunk,
        verse: verse.reference.verse,
      });

      if (index < chunks.length - 1) {
        pushCurrentParagraph();
      }
    });
  }

  pushCurrentParagraph();

  return paragraphs;
}

export function buildRenderableScriptureContent(
  scriptureReferences: Array<{ esvText?: string | null }> | null | undefined,
): RenderableScriptureContent {
  if (!scriptureReferences || scriptureReferences.length === 0) {
    return {
      paragraphs: [],
      emptyState: SCRIPTURE_EMPTY_STATE_COPY,
    };
  }

  const verseParagraphs = scriptureReferences.flatMap((reference) =>
    buildRenderableParagraphsFromVerses(parseScriptureVerses(reference.esvText)),
  );

  if (verseParagraphs.length > 0) {
    return {
      paragraphs: verseParagraphs,
      emptyState: null,
    };
  }

  const plainTextParagraphs = scriptureReferences.flatMap((reference) =>
    splitPlainTextIntoParagraphs(reference.esvText),
  );

  if (plainTextParagraphs.length > 0) {
    return {
      paragraphs: plainTextParagraphs.map((paragraph) => ({
        segments: [{ content: paragraph, verse: null }],
      })),
      emptyState: null,
    };
  }

  return {
    paragraphs: [],
    emptyState: SCRIPTURE_EMPTY_STATE_COPY,
  };
}

export function buildCombinedScriptureText(
  scriptureReferences: Array<{ esvText?: string | null }> | null | undefined,
): string {
  if (!scriptureReferences || scriptureReferences.length === 0) {
    return SCRIPTURE_EMPTY_STATE_COPY;
  }

  const parsedVerses = scriptureReferences.flatMap((reference) => parseScriptureVerses(reference.esvText));
  if (parsedVerses.length > 0) {
    return JSON.stringify(parsedVerses);
  }

  const plainText = scriptureReferences
    .map((reference) => reference.esvText?.trim())
    .filter((text): text is string => Boolean(text));

  return plainText.length > 0 ? plainText.join("\n\n") : SCRIPTURE_EMPTY_STATE_COPY;
}
